// src/utils/venueTransformer.ts

import { Venue } from '../services/venues.api';
import venuesAPI from '../services/venues.api';

const theme = {
  secondaryBlue: '#0066CC',
  accentPurple: '#6B2D8C',
  govBlue: '#003B7A',
  warning: '#F59E0B',
};

// ✅ NEW: Timing slot configuration interface
export interface TimingSlotConfig {
  slotType: 'four_hours' | 'eight_hours' | 'custom';
  ratePerFourHours: number;
  fullDayRate: number;
  description: string;
  availableStartTimes: string[];
  minBookingHours: number;
  maxBookingHours: number;
}

export interface UIVenue extends Venue {
  color: string;
  bookings: number;
  availability: string;
  isMovieShooting?: boolean;
  entryFee?: number;
  fixedSecurityDeposit?: number;
  rate8Hours?: number;
  nightRateMultiplier?: number;
  image: string; // Required - full backend URL
  
  // ✅ NEW: Timing slot properties for shooting venues
  hasTimingSlots?: boolean;
  timingSlotConfig?: TimingSlotConfig;
  termsAndConditions?: string[];

}

/**
 * Transform API venue to UI venue with additional display properties
 */
export const transformVenueForUI = (venue: Venue): UIVenue => {
  // Determine color based on venue type and name
  let color = theme.secondaryBlue;
  
  if (venue.type === 'event') {
    color = theme.govBlue;
  } else {
    // Shooting venues
    if (venue.name.toLowerCase().includes('lumbini')) {
      color = theme.govBlue;
    } else if (venue.name.toLowerCase().includes('necklace')) {
      color = theme.accentPurple;
    } else if (venue.name.toLowerCase().includes('sanjeevaiah')) {
      color = theme.secondaryBlue;
    } else if (venue.name.toLowerCase().includes('movie')) {
      color = theme.warning;
    }
  }

  // Get full backend image URL
  const imageUrl = venuesAPI.getImageUrl(venue.image) || '/images/default-venue.jpg';

  // Parse rate to number (in case it comes as string from API)
  const rate = typeof venue.rate === 'string' ? parseFloat(venue.rate) : venue.rate;

  // Build UI venue
  const uiVenue: UIVenue = {
    ...venue,
    rate, // Ensure rate is number
    image: imageUrl, // Full backend URL
    description: venue.description || 'No description available',
    color,
    bookings: venue.totalBookings || 0,
    availability: venue.status === 'active' ? 'Available' : 
                  venue.status === 'maintenance' ? 'Under Maintenance' : 
                  'Not Available',
    // Charges (prefix/suffix rate, security deposit, GST) are published per venue
    // in `venue.metadata` and spread in above — read them via src/utils/venueCharges.
    isMovieShooting: venue.name.toLowerCase().includes('movie shooting'),
    
    // ✅ NEW: Pass through timing slot properties from API
    hasTimingSlots: venue.hasTimingSlots || false,
    timingSlotConfig: venue.timingSlotConfig,
      termsAndConditions: venue.termsAndConditions || [],

  };

  // Necklace Road event venue has no security deposit
  if (venue.type === 'event' && venue.name.toLowerCase().includes('necklace')) {
    uiVenue.fixedSecurityDeposit = 0;
  }

  // Sanjeevaiah Park has fixed security deposit of ₹3,000 for events.
  // ⚠️ This is the one charge NOT yet published in `venue.metadata`, so this
  // name-match must stay until the backend publishes it — without it the venue
  // would quote a ₹0 deposit.
  if (venue.type === 'event' && venue.name.toLowerCase().includes('sanjeevaiah')) {
    uiVenue.fixedSecurityDeposit = 3000;
  }

  // Add shooting-specific properties
  if (venue.type === 'shooting') {
    uiVenue.rate8Hours = rate * 2; // 8 hours is double the 4-hour rate
    uiVenue.nightRateMultiplier = 2;

    // Lumbini Park has entry fee
    if (venue.name.toLowerCase().includes('lumbini')) {
      uiVenue.entryFee = 5000;
    }

  }

  return uiVenue;
};

/**
 * Transform array of API venues to UI venues
 */
export const transformVenuesForUI = (venues: Venue[]): UIVenue[] => {
  return venues.map(transformVenueForUI);
};

/**
 * Get venue by ID from transformed list
 */
export const getVenueById = (venues: UIVenue[], id: string): UIVenue | undefined => {
  return venues.find(v => v.id === id);
};

/**
 * Filter venues by type
 */
export const filterVenuesByType = (
  venues: UIVenue[], 
  type: 'event' | 'shooting' | 'all'
): UIVenue[] => {
  if (type === 'all') return venues;
  return venues.filter(v => v.type === type);
};

/**
 * Filter available venues only
 */
export const getAvailableVenues = (venues: UIVenue[]): UIVenue[] => {
  return venues.filter(v => v.status === 'active');
};

/**
 * Get shooting venues with timing slots
 */
export const getShootingVenuesWithSlots = (venues: UIVenue[]): UIVenue[] => {
  return venues.filter(v => v.type === 'shooting' && v.hasTimingSlots);
};

/**
 * Sort venues by bookings (most popular first)
 */
export const sortByPopularity = (venues: UIVenue[]): UIVenue[] => {
  return [...venues].sort((a, b) => b.bookings - a.bookings);
};

/**
 * Sort venues by rate (low to high)
 */
export const sortByRate = (venues: UIVenue[], ascending = true): UIVenue[] => {
  return [...venues].sort((a, b) => {
    const rateA = typeof a.rate === 'string' ? parseFloat(a.rate) : a.rate;
    const rateB = typeof b.rate === 'string' ? parseFloat(b.rate) : b.rate;
    return ascending ? rateA - rateB : rateB - rateA;
  });
};